import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, Plus } from "react-feather";
import { Button, Spinner } from "@cinesys/ui/components";
import MetadataImportHeader from "./MetadataImportHeader";
import {
  Chip,
  Page,
  PageHead,
  PageTitle,
  StatusPill,
  SubNote,
  WorkflowBadge,
  Wrap,
} from "./ui";
import { FORMAT_LABEL, MI_INTERFACE_ID } from "./config";
import { ImportProfile } from "./mockData";
import { useImportProfiles } from "./service";
import NewProfileModal from "./NewProfileModal";
import { useInterfaceAccess } from "../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../components/InterfaceAccessNotice";
import { DelayedSpinner } from "@cinesys/experience-ui/components";

/**
 * Metadata Workflows — the interface's landing screen. A list of persistent workflows (configured
 * automations), not a one-off upload form: each row shows what the workflow does, its cadence, its
 * status, and its last run. "+ New Workflow" and per-row "Run Now" / "Open" actions.
 */

const HeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 18px;
`;

const RowCard = styled.div`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 14px;
  box-shadow: var(--miShadow);
  padding: 16px 18px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px 20px;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Name = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #fff;
  text-align: left;

  &:hover {
    color: var(--miAccentBright);
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 9px;
`;

const Desc = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--miInkFaint);
  max-width: 80ch;
`;

const LastRun = styled.div`
  margin-top: 11px;
  font-size: 13px;
  color: var(--miInkSoft);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  b {
    color: #fff;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .muted {
    color: var(--miInkFaint);
  }
  .warn {
    color: var(--miWarn);
  }
  .danger {
    color: var(--miDanger);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: end;

  @media (max-width: 640px) {
    justify-self: start;
  }
`;

const Queued = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  color: var(--miAccentBright);
`;

const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #052e1a;
  background: var(--miAccentBright);
  border-radius: 999px;
  padding: 3px 9px;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #052e1a;
  }
`;

/** Saved-profile load failure. Deliberately does not imply deletion: it's a load failure, not a
 *  data-loss event. */
const LoadError = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 18px;
  padding: 14px 16px;
  border: 1px solid var(--miLine);
  border-left: 3px solid var(--miWarn);
  border-radius: 12px;
  background: var(--miRaised);

  .msg {
    font-size: 13.5px;
    color: var(--miInkSoft);
  }
  .sub {
    margin-top: 3px;
    font-size: 12.5px;
    color: var(--miInkFaint);
  }
`;

function cadenceLabel(p: ImportProfile): string {
  if (p.schedule.mode === "manual") {
    return "Manual";
  }
  if (p.schedule.cadence === "weekly") {
    return `Weekly · ${p.schedule.weekday ?? ""}`.trim();
  }
  return `Daily · ${p.schedule.at ?? ""}`.trim();
}

function ProfileRow({ profile }: { profile: ImportProfile }) {
  const navigate = useNavigate();
  const [queued, setQueued] = useState(false);
  const open = () => navigate(`${profile.id}`);
  const runnable = profile.status !== "draft";

  return (
    <RowCard>
      <div>
        <TitleLine>
          <Name onClick={open}>{profile.name}</Name>
          <StatusPill status={profile.status} />
          <LiveBadge>Live · Sandbox</LiveBadge>
        </TitleLine>
        <Meta>
          <WorkflowBadge mode={profile.workflow} />
          <Chip>{FORMAT_LABEL[profile.format]}</Chip>
          <Chip>{cadenceLabel(profile)}</Chip>
        </Meta>
        <Desc>{profile.description}</Desc>
        <LastRun>
          {profile.status === "draft" ? (
            <span className="muted">
              Configuration incomplete — not run yet
            </span>
          ) : profile.lastRun ? (
            <>
              <span className="muted">Last run {profile.lastRun.date}</span>
              <span>·</span>
              <span>
                <b>{profile.lastRun.changed}</b> changed of{" "}
                <b>{profile.lastRun.total}</b>
              </span>
              {profile.lastRun.status === "failed" && (
                <span className="danger">· failures</span>
              )}
              {profile.lastRun.status === "warning" && (
                <span className="warn">· warnings</span>
              )}
            </>
          ) : (
            <span className="muted">Not run yet</span>
          )}
        </LastRun>
      </div>
      <Actions>
        {queued ? (
          <Queued>Run queued · demo</Queued>
        ) : (
          <Button
            label="Run Now"
            icon={Play}
            type="default"
            disabled={!runnable}
            onClick={() => setQueued(true)}
          />
        )}
        <Button label="Open" icon={ArrowRight} type="success" onClick={open} />
      </Actions>
    </RowCard>
  );
}

const OverviewScreen = () => {
  const access = useInterfaceAccess(MI_INTERFACE_ID);
  const query = useImportProfiles();
  const [creating, setCreating] = useState(false);

  // A failed load is an ERROR STATE — never flattened into "no saved profiles."
  const rows: ImportProfile[] = query.data ?? [];

  // Org-level entitlement gate — see `CandidateAssignmentsScreen.tsx`'s equivalent. Checked
  // before this screen's own query loading below.
  if (access.status !== "granted") {
    return (
      <Page>
        <MetadataImportHeader />
        {access.status === "denied" ? (
          <InterfaceAccessNotice reason={access.reason} />
        ) : (
          <DelayedSpinner padTop />
        )}
      </Page>
    );
  }

  return (
    <Page>
      {creating && <NewProfileModal onClose={() => setCreating(false)} />}
      <MetadataImportHeader />
      <Wrap>
        <HeadRow>
          <div>
            <PageHead>
              <PageTitle>Metadata Workflows</PageTitle>
            </PageHead>
            <SubNote>
              Configured metadata import &amp; enrichment automations for this
              organization. Collection Sync workflows reconcile a shared
              metadata set on a schedule; Asset Import workflows match rows to
              individual assets for review before writing.
            </SubNote>
          </div>
          <Button
            label="New Workflow"
            icon={Plus}
            type="success"
            onClick={() => setCreating(true)}
          />
        </HeadRow>

        {query.isLoading ? (
          <Spinner padTop />
        ) : (
          <>
            {query.isError && (
              <LoadError role="alert">
                <div>
                  <div className="msg">Saved profiles couldn’t be loaded.</div>
                  <div className="sub">
                    This is a temporary loading problem — your saved profiles
                    have not been removed.
                  </div>
                </div>
                <Button
                  label={query.isFetching ? "Retrying…" : "Retry"}
                  type="default"
                  disabled={query.isFetching}
                  onClick={() => query.refetch()}
                />
              </LoadError>
            )}
            <List>
              {rows.map((p) => (
                <ProfileRow key={p.id} profile={p} />
              ))}
            </List>
          </>
        )}
      </Wrap>
    </Page>
  );
};

export default OverviewScreen;
